import { useMemo, useState } from 'react'

import { Edition } from '../../models/Edition'
import { RecordDataMap } from '../../models/RecordDataMap'
import { Icon } from '../Icon'
import { FullScreenVideoModal } from './FullScreenVideoModal'
import Button from './design/Button'
import Heading from './design/Heading'
import Label from './design/Label'
import Paragraph from './design/Paragraph'
import Tag from './design/Tag'

type Props = {
    plan: RecordDataMap
    edition?: Edition
    onPlanSelected: (plan: RecordDataMap) => void
    allowFreeTrial?: boolean
}

export const PlanCardView = ({ plan, edition, onPlanSelected, allowFreeTrial }: Props) => {
    const planId = plan.plan
    const costPerMonth = planId !== 'free' ? plan.price_display : 'Free forever'
    const planBanner = plan.banner

    const buttonBody = useMemo(() => {
        const buttonParts = plan.subscribe_button_title.split('\n')
        return (
            <div className="flex flex-col gap-y-1">
                <Label size="lg">{buttonParts[0]}</Label>
                {buttonParts && buttonParts.length && (
                    <Paragraph size="sm">{buttonParts[1]}</Paragraph>
                )}
            </div>
        )
    }, [plan])

    let isSmallButton = true

    const benefits = plan.benefits
    const firstLine =
        planId !== 'free' ? benefits.find((benefit: any) => !benefit.leading_image) : undefined

    const [showVideoPlayer, setShowVideoPlayer] = useState(false)
    const [videoUri, setVideoUri] = useState<string>()
    const playVideo = (videoUrl: string) => {
        setVideoUri(videoUrl)
        setShowVideoPlayer(true)
    }

    return (
        <>
            <div className="rounded-lg bg-white">
                {planBanner && (
                    <div className="bg-warning-100 rounded-t-lg py-1.5 text-center text-[13px] font-semibold text-gray-600 uppercase">
                        {planBanner}
                    </div>
                )}
                <div className="p-8">
                    <div className="mb-6 flex">
                        {plan.premium_sales_image_uri && (
                            <div className="relative mr-6 w-[224px]">
                                <img
                                    src={plan.premium_sales_image_uri}
                                    className="w-[200px] rounded-lg"
                                />
                                {plan.premium_sales_video_uri && (
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                                        <Button
                                            size="small"
                                            intent="secondary"
                                            className="whitespace-nowrap"
                                            onClick={() =>
                                                playVideo(
                                                    edition?.videoUri ??
                                                        plan.premium_sales_video_uri!
                                                )
                                            }
                                        >
                                            Watch Video
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="grow">
                            <div className="mb-3 flex justify-between">
                                <Heading size="sm">
                                    {plan.subtitle ? plan.subtitle : plan.title}
                                </Heading>
                                <Tag
                                    size="sm"
                                    intent="muted"
                                    className={plan.price_show_as_discounted ? 'line-through' : ''}
                                    label={costPerMonth}
                                />
                            </div>

                            {firstLine && (
                                <Label size="md" className="text-gray-700">
                                    {firstLine.message}
                                </Label>
                            )}
                            <ul className="my-3 flex flex-col gap-2">
                                {benefits
                                    .filter(
                                        (benefit: any) => firstLine?.message !== benefit.message
                                    )
                                    .map((benefit: any, index: number) => (
                                        <li
                                            key={`${planId}-${index}`}
                                            className="flex items-center justify-start"
                                        >
                                            <Icon icon="checkmark.small" className="shrink-0" />
                                            <span className="pl-3">{benefit.message}</span>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    </div>

                    <Button
                        intent={plan.subscribe_button_color === 'dark' ? 'primary' : 'secondary'}
                        size={isSmallButton ? 'small' : 'medium'}
                        onClick={() => onPlanSelected(plan)}
                        className="h-[76px] w-full py-4"
                    >
                        {buttonBody}
                    </Button>

                    {allowFreeTrial && planId !== 'free' && (
                        <Paragraph className="mt-6 text-center text-gray-600">
                            {`$${plan.price} per month after free trial`}
                        </Paragraph>
                    )}
                </div>
            </div>
            <FullScreenVideoModal
                isOpen={showVideoPlayer}
                title="Video"
                hlsUrl={videoUri ?? ''}
                onClose={() => setShowVideoPlayer(false)}
            />
        </>
    )
}
